import * as React from 'react';
import {
  Box,
  ParagraphText,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import { get } from 'lodash-es';
import { IconButton } from '@web-config-app/core-react-ui';
import { useIntl } from '@leagueplatform/locales';
import { useEntityData } from '@web-config-app/core-react';
import { useDataPoints, DataPointModal } from '@web-config-app/data-points';
import { useEntityDetailsProps } from '@web-config-app/entity-form';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { ConditionOperatorValue } from './condition-operator-value.component';
import { GoToPicker } from './go-to-picker.component';
import { Pill } from './pill.component';
import { RemovableChip } from './removable-chip.component';
import { SourcePicker } from './source-picker.component';
import {
  resolveSubjectValueType,
  resolveCatalogOptions,
  findReferencedItem,
  flattenDataPointLeaves,
} from './source-resolution';
import type {
  ConditionalActionCondition,
  ConditionalActionConditionSubject,
} from './types';

interface ConditionalActionConditionItemProps {
  condition: ConditionalActionCondition;
  /**
   * Position of this condition within the rule's `conditions` array.
   * When > 0 the item renders a leading "and" connector.
   */
  index: number;
  /**
   * Subject declarations available to this condition, sourced from the
   * conditional-action control's `x-entity-control.options.conditionalAction.conditionSubjects`.
   * When the list has a single entry, the subject renders as a static
   * pill. When it has multiple entries, the subject renders as a
   * `<Select>` so the author can switch subjects.
   */
  conditionSubjects: ConditionalActionConditionSubject[];
  /**
   * Optional allow-list of operator IDs offered by this schema instance
   * (`x-entity-control.options.conditionalAction.operators`). `undefined` = no
   * restriction. Stored conditions whose operator falls outside the
   * allow-list still render their saved operator so the author can see
   * and change it.
   */
  allowedOperators: string[] | undefined;
  /**
   * Optional per-instance operator label-key overrides
   * (`x-entity-control.options.conditionalAction.labelOverrides`), keyed by
   * operator ID.
   */
  operatorLabelOverrides: Record<string, string> | undefined;
  /**
   * Data of the array item the conditional-action control is editing (e.g. the
   * enclosing question). For `currentArrayItem` subjects this IS the resolution
   * item; for `dataSource` / `dataPoint` subjects it's used to compute the
   * backward-only picker scope. Resolved once by the control and passed
   * down.
   */
  enclosingItemData: unknown;
  /**
   * Full entity edit path to the rule's `conditions` field — the base
   * for the rendered fields' unique `id`/`name`s (a11y/autofill).
   */
  conditionsEditPath: string;
  enabled: boolean;
  /** Replace this condition with `next`. */
  onUpdate: (next: ConditionalActionCondition) => void;
  /** Remove this condition from the rule. */
  onRemove: () => void;
  /**
   * Whether the per-condition remove button renders. The rule card sets this
   * to `false` when a rule has exactly one condition, since removing the sole
   * condition would leave an empty rule — the rule-level trash is the only
   * appropriate delete affordance at that point. Defaults to `true`.
   */
  canRemove?: boolean;
}

/**
 * Renders one condition inside a conditional-action rule's IF row:
 * `[and] · <subject> · <operator> · <value(s)> · ×`
 *
 * Owns its own remove affordance — the parent does NOT render trashes
 * separately. Multiple conditions within the same rule are implicitly
 * ANDed; there's no per-condition combinator field.
 */
export const ConditionalActionConditionItem: React.FC<
  ConditionalActionConditionItemProps
> = ({
  condition,
  index,
  conditionSubjects,
  allowedOperators,
  operatorLabelOverrides,
  enclosingItemData,
  conditionsEditPath,
  enabled,
  onUpdate,
  onRemove,
  canRemove = true,
}) => {
  const { formatMessage } = useIntl();

  const activeSubject = conditionSubjects.find(
    (subject) => subject.type === condition.subjectType,
  );
  const isUnrecognizedSubject = !activeSubject;

  // The member data points a `dataPoint` subject can gate on (e.g. `up.age`),
  // resolved off the config/DML registry and filtered by the subject's
  // declared `dataTypes`. Read from the subject declaration regardless of the
  // active subject so the hook order is stable; empty config resolves to `[]`.
  const dataPointSubject = conditionSubjects.find(
    (subject) => subject.type === 'dataPoint',
  );
  const dataPointConfig = dataPointSubject?.['x-entity-data-point'];
  const { rootSchema, currentAuthoringEnvironment, entityType } =
    useEntityDetailsProps();
  const {
    dataPoints: dataPointFlows,
    isError: dataPointsError,
    isLoading: dataPointsLoading,
  } = useDataPoints({
    schema: { 'x-entity-data-point': dataPointConfig } as AnnotatedJsonSchema,
    rootSchema,
    currentAuthoringEnvironment,
    entityType,
  });
  const dataPointOptions = React.useMemo(
    () => flattenDataPointLeaves(dataPointFlows),
    [dataPointFlows],
  );
  // The data point the condition currently targets (by `ref`) — the item its
  // value type is read off, exactly as a referenced question is for `dataSource`.
  const selectedDataPoint = React.useMemo(
    () => dataPointOptions.find((point) => point.id === condition.ref),
    [dataPointOptions, condition.ref],
  );

  // Dispatch on the subject variant to pick the resolution item:
  //  - currentArrayItem → the item the control is editing (e.g. this question);
  //  - dataSource → another entity item picked from a registered data
  //    source, looked up by `ref` via `findReferencedItem`;
  //  - dataPoint → the member data point picked by `ref` from the registry.
  const dataSourceConfig =
    activeSubject?.type === 'dataSource'
      ? activeSubject['x-entity-data-source']
      : undefined;
  const entityData = useEntityData();
  const referencedItem = React.useMemo(() => {
    if (!dataSourceConfig || !condition.ref) return undefined;
    const valuePath = dataSourceConfig.mapping?.optionValuePath ?? 'linkId';
    // Shape gate: even inside entityData, a linkId match alone is not enough —
    // action payloads (`{ linkId: "..." }` on `goTo` actions) live deeper and
    // can shadow the real question in the DFS. Require the matched node to
    // also carry the subject's declared resolution paths (e.g. `answerType`,
    // `choiceOptions.answerOptions`), which no payload has.
    const valueTypeFromPath =
      activeSubject && 'valueTypeFrom' in activeSubject
        ? activeSubject.valueTypeFrom.path
        : undefined;
    const requiredPaths = [
      activeSubject?.valueCatalog?.path,
      valueTypeFromPath,
    ].filter((path): path is string => !!path);
    return findReferencedItem(
      entityData,
      valuePath,
      condition.ref,
      requiredPaths,
    );
  }, [dataSourceConfig, condition.ref, entityData, activeSubject]);

  const resolutionData = (() => {
    switch (activeSubject?.type) {
      case 'currentArrayItem':
        return enclosingItemData;
      case 'dataSource':
        return referencedItem;
      case 'dataPoint':
        return selectedDataPoint;
      default:
        return undefined;
    }
  })();

  // The enclosing question's own ref value (its linkId). A `dataSource`
  // subject may only point at EARLIER questions, so the subject picker is
  // scoped to options before this one (see `limitToBefore`); it also can't
  // reference itself.
  const subjectValuePath =
    dataSourceConfig?.mapping?.optionValuePath ?? 'linkId';
  const enclosingRefValue = dataSourceConfig
    ? get(enclosingItemData, subjectValuePath)
    : undefined;
  const currentQuestionRef =
    typeof enclosingRefValue === 'string' ? enclosingRefValue : undefined;

  // Resolve the active subject's value type per-condition. When subjects
  // declare different value types and the author switches the subject
  // dropdown, this recomputes against the new subject's declaration —
  // unlike a single rule-level resolution that would freeze on the
  // first subject's type.
  const valueType = resolveSubjectValueType(resolutionData, activeSubject);

  // The comparison-value options for the active subject, read off the
  // resolution item (enclosing question, or the referenced one). Memoized
  // so the pickers get a stable array between renders that don't touch the
  // item data or the active subject.
  const catalogOptions = React.useMemo(
    () => resolveCatalogOptions(resolutionData, activeSubject),
    [resolutionData, activeSubject],
  );

  // Subject labels are TranslationObjects ({ text } or { key }) — plain
  // text wins, otherwise format the locale key.
  const subjectLabel = (subject: ConditionalActionConditionSubject) =>
    subject.label.text ?? formatMessage({ id: subject.label.key });

  // Full edit path of THIS condition — base for unique field ids/names
  // (a11y/autofill) and the scoped-data-source anchor for value pickers.
  const conditionEditPath = `${conditionsEditPath}.${index}`;

  // The source picker — which subject variant the condition tests. Rendered as
  // a pill-styled action picker when the control offers more than one subject
  // (e.g. a question vs. a data point); a single-subject control keeps the
  // static pill. Swapping the source clears the picked ref + operator inputs
  // (the new subject has a different value catalog / type).
  const renderSourcePicker = () => {
    if (conditionSubjects.length > 1) {
      return (
        <SourcePicker
          value={condition.subjectType}
          options={conditionSubjects.map((subject) => ({
            value: subject.type,
            label: subjectLabel(subject),
          }))}
          menuLabel={formatMessage({ id: 'CONDITIONAL_ACTION_SOURCE_LABEL' })}
          enabled={enabled}
          onChange={(nextSubjectType) => {
            // Reselecting the source that's already active is a no-op — don't
            // wipe the authored ref/operator/inputs of a completed condition.
            if (nextSubjectType === condition.subjectType) return;
            onUpdate({
              subjectType: nextSubjectType,
              ref: undefined,
              inputs: {},
            });
          }}
        />
      );
    }
    if (activeSubject) {
      return <Pill>{subjectLabel(activeSubject)}</Pill>;
    }
    return null;
  };

  // The picker that follows the source, chosen by the active subject:
  //  - dataSource → the referenced-question picker (which earlier question);
  //  - dataPoint → the member data-point picker (which data point);
  //  - currentArrayItem → none; the subject IS the enclosing question.
  // Hidden labels keep native <select> semantics without adding visible label
  // text to the sentence layout.
  const renderDependentPicker = () => {
    if (dataSourceConfig) {
      return (
        <>
          <VisuallyHidden as="label" htmlFor={`${conditionEditPath}.ref`}>
            {formatMessage({ id: 'CONDITIONAL_ACTION_SUBJECT_QUESTION_LABEL' })}
          </VisuallyHidden>
          <GoToPicker
            id={`${conditionEditPath}.ref`}
            value={condition.ref}
            onChange={(next) =>
              onUpdate({
                subjectType: condition.subjectType,
                ref: next,
                inputs: {},
              })
            }
            placeholderKey="CONDITIONAL_ACTION_CHOOSE_SUBJECT_QUESTION"
            enabled={enabled}
            dataSourceName={dataSourceConfig.dataSource}
            dataSourceType={dataSourceConfig.type ?? 'enumOptions'}
            mapping={dataSourceConfig.mapping}
            limitToBefore={currentQuestionRef}
          />
        </>
      );
    }
    if (activeSubject?.type === 'dataPoint') {
      // Data points form a tree (namespaced groups → leaves) and the catalog
      // grows over time, so pick from the shared browse modal rather than a
      // flat select — consistent with every other data-point picker in CACT.
      // The current selection shows as its label; picking a new one clears the
      // operator/inputs (a different point can carry a different value type).
      return (
        <Box
          css={{ display: 'inline-flex', alignItems: 'center', gap: '$half' }}
        >
          {selectedDataPoint ? (
            // Selected: a removable chip (label + ×) shared with the value
            // pills. The whole chip clears the pick; to change it, remove and
            // choose again (keeps the sentence uncluttered).
            <RemovableChip
              label={selectedDataPoint.label}
              removeLabel={formatMessage(
                { id: 'CONDITIONAL_ACTION_REMOVE_DATA_POINT_ARIA' },
                { label: selectedDataPoint.label },
              )}
              enabled={enabled}
              onRemove={() =>
                onUpdate({
                  subjectType: condition.subjectType,
                  ref: undefined,
                  inputs: {},
                })
              }
            />
          ) : (
            // Empty: the browse modal's trigger IS the "choose" affordance.
            <DataPointModal
              dataPoints={dataPointFlows}
              triggerLabel={formatMessage({
                id: 'CONDITIONAL_ACTION_CHOOSE_DATA_POINT',
              })}
              onSelected={(id?: string) =>
                onUpdate({
                  subjectType: condition.subjectType,
                  ref: id || undefined,
                  inputs: {},
                })
              }
              disabled={!enabled}
              isError={dataPointsError}
              isLoading={dataPointsLoading}
            />
          )}
        </Box>
      );
    }
    return null;
  };

  const connector = (
    <Box>
      <ParagraphText emphasis="subtle">
        {formatMessage({
          id:
            index === 0
              ? 'CONDITIONAL_ACTION_IF_LABEL'
              : 'CONDITIONAL_ACTION_AND_LABEL',
        })}
      </ParagraphText>
    </Box>
  );

  // A subject with a dependent picker (a question / data-point dropdown) gets
  // its own line with the operator + value beneath it, since those dropdowns
  // can be wide; the current-question subject has no dependent picker and stays
  // inline ("If answer is one of …"). Unrecognized subjects render a dashed
  // placeholder pill.
  const renderConditionBody = () => {
    if (isUnrecognizedSubject) {
      return (
        <>
          {connector}
          <Box
            css={{
              paddingX: '$half',
              paddingY: '$quarter',
              borderRadius: '$pill',
              border:
                'dashed $colors$onSurfaceBorderSubdued $borderWidths$thin',
              backgroundColor: '$surfaceBackgroundSecondary',
            }}
          >
            <ParagraphText emphasis="subtle">
              {formatMessage({
                id: 'CONDITIONAL_ACTION_UNRECOGNIZED_CONDITION',
              })}
            </ParagraphText>
          </Box>
        </>
      );
    }
    const operatorValue = (
      <ConditionOperatorValue
        condition={condition}
        valueType={valueType}
        catalogOptions={catalogOptions}
        allowedOperators={allowedOperators}
        operatorLabelOverrides={operatorLabelOverrides}
        conditionEditPath={conditionEditPath}
        enabled={enabled}
        onUpdate={onUpdate}
      />
    );
    const dependentPicker = renderDependentPicker();
    if (dependentPicker) {
      return (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            minWidth: 0,
            gap: '$half',
          }}
        >
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '$half',
            }}
          >
            {connector}
            {renderSourcePicker()}
            {dependentPicker}
          </Box>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '$half',
            }}
          >
            {operatorValue}
          </Box>
        </Box>
      );
    }
    return (
      <>
        {connector}
        {renderSourcePicker()}
        {operatorValue}
      </>
    );
  };

  return (
    <Box
      css={{
        // Grow to fill the remaining horizontal space in the parent's row.
        // Split into [sentence content | trash]: the content wraps
        // internally while the trash stays pinned to the top-right
        // (`alignItems: flex-start`), instead of drifting down with the
        // wrapped value chips.
        display: 'flex',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minWidth: 0,
        alignItems: 'flex-start',
        gap: '$half',
      }}
    >
      <Box
        css={{
          display: 'flex',
          flexGrow: 1,
          minWidth: 0,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '$half',
        }}
      >
        {renderConditionBody()}
      </Box>
      {enabled && canRemove && (
        <Box>
          <IconButton
            tooltip={formatMessage({
              id: 'CONDITIONAL_ACTION_REMOVE_CONDITION_ARIA',
            })}
            label={formatMessage({
              id: 'CONDITIONAL_ACTION_REMOVE_CONDITION_ARIA',
            })}
            onClick={onRemove}
            icon="interfaceDelete"
            size="small"
            priority="secondary"
            quiet
            destructive
          />
        </Box>
      )}
    </Box>
  );
};
